'use client';

import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SPARTAN_PRODUCT_EXPLORE_FEATURES_DETAIL_CHANGE_EVENT } from '@/app/lcv/components/sections/spartan/products/explore-features/constants';
import type {
  SpartanProductExploreFeaturesDetailData,
  SpartanProductExploreFeaturesDetailsMap,
  UseExploreFeaturesModalParams,
} from '@/app/lcv/components/sections/spartan/products/explore-features/types';
import {
  getSpartanExploreFeaturesDetailId,
  setSpartanExploreFeaturesDetailId,
} from '@/app/lcv/components/sections/spartan/products/explore-features/utils';

const EMPTY_DETAILS: SpartanProductExploreFeaturesDetailsMap = {};

export const useExploreFeaturesModal = ({
  features,
  details = EMPTY_DETAILS,
}: UseExploreFeaturesModalParams) => {
  const pathname = usePathname();
  const openTargetRef = useRef<HTMLElement | null>(null);
  const [activeDetailId, setActiveDetailId] = useState<string | null>(null);

  const activeFeature = useMemo(
    () => features.find((feature) => feature.id === activeDetailId) ?? null,
    [activeDetailId, features]
  );

  const activeDetail = useMemo<SpartanProductExploreFeaturesDetailData | null>(
    () => (activeDetailId ? (details[activeDetailId] ?? null) : null),
    [activeDetailId, details]
  );

  const isOpen = !!activeFeature && !!activeDetail;

  const captureOpenTarget = useCallback(() => {
    const activeElement = document.activeElement;
    if (
      activeElement instanceof HTMLElement &&
      activeElement !== document.body &&
      activeElement !== document.documentElement
    ) {
      openTargetRef.current = activeElement;
    }
  }, []);

  const syncFromUrl = useCallback(() => {
    const detailId = getSpartanExploreFeaturesDetailId();
    const hasMatchingFeature = features.some(
      (feature) => feature.id === detailId
    );

    if (detailId && details[detailId] && hasMatchingFeature) {
      setActiveDetailId((currentDetailId) => {
        if (!currentDetailId) captureOpenTarget();
        return detailId;
      });
      return;
    }
    setActiveDetailId(null);

    if (detailId) {
      setSpartanExploreFeaturesDetailId(pathname ?? '', null);
    }
  }, [captureOpenTarget, details, features, pathname]);

  useEffect(() => {
    syncFromUrl();
    window.addEventListener('popstate', syncFromUrl);
    window.addEventListener(
      SPARTAN_PRODUCT_EXPLORE_FEATURES_DETAIL_CHANGE_EVENT,
      syncFromUrl
    );
    return () => {
      window.removeEventListener('popstate', syncFromUrl);
      window.removeEventListener(
        SPARTAN_PRODUCT_EXPLORE_FEATURES_DETAIL_CHANGE_EVENT,
        syncFromUrl
      );
    };
  }, [syncFromUrl]);

  const openDetail = useCallback(
    (detailId: string) => {
      const hasMatchingFeature = features.some(
        (feature) => feature.id === detailId
      );
      if (!details[detailId] || !hasMatchingFeature) return;

      captureOpenTarget();
      setActiveDetailId(detailId);
      setSpartanExploreFeaturesDetailId(pathname ?? '', detailId);
    },
    [captureOpenTarget, details, features, pathname]
  );

  const closeDetail = useCallback(() => {
    setActiveDetailId(null);
    setSpartanExploreFeaturesDetailId(pathname ?? '', null);
  }, [pathname]);

  const restoreOpenTargetFocus = useCallback(() => {
    const openTarget = openTargetRef.current;
    openTargetRef.current = null;
    if (openTarget?.isConnected) openTarget.focus({ preventScroll: true });
  }, []);

  return {
    isOpen,
    activeFeature,
    activeDetail,
    openDetail,
    closeDetail,
    restoreOpenTargetFocus,
  };
};
